/**
 * Phase SC-22d — team pay + attendance API client.
 *
 * Wraps `/api/team-pay/*` in eduviaos-backend (`app/domains/team_pay`).
 * Wire shapes mirror `app/domains/team_pay/schemas.py`; a field change on
 * the BE must land here too. Every amount is integer PAISE; every `date`
 * is an IST calendar day as `YYYY-MM-DD`.
 *
 * All routes sit behind the `payroll` module gate and the admin-ceiling
 * permissions `view_team_pay` / `manage_team_pay` /
 * `manage_team_attendance` / `approve_team_attendance`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

export {
  formatPaise,
  formatPaiseCompact,
  paiseToRupees,
  rupeesToPaise,
} from "@/lib/accounting/api";

// ── Vocabularies ───────────────────────────────────────────────────────

export type PayBasis = "monthly" | "daily" | "hourly";
export type PaymentCadence = "monthly" | "weekly" | "ad_hoc";
export type ApprovalMode =
  | "admin_approves_each"
  | "self_auto_approve"
  | "geo_auto_approve"
  | "admin_marks_only";
export type AttendanceStatus = "present" | "half_day" | "absent" | "leave" | "holiday" | "week_off";
export type ApprovalStatus = "pending" | "approved" | "rejected";
export type AttendanceSource = "self_geo" | "self_manual" | "admin" | "leave_request";
export type DayBasis = "calendar_days" | "working_days";
export type RecoveryPlan = "same_month" | "next_month" | "emi" | "manual";
export type AdjustmentKind = "incentive" | "bonus" | "deduction" | "reimbursement";
export type TeamPaymentMethod = "cash" | "upi" | "bank" | "other";
export type AllocationMode = "auto" | "all_salary" | "all_advance";
export type AllocationTarget = "salary" | "dues" | "advance";
export type LeaveType = "casual" | "sick" | "earned" | "unpaid" | "other";
export type LeaveStatus = "pending" | "approved" | "rejected" | "cancelled";

export const PAY_BASIS_LABELS: Record<PayBasis, string> = {
  monthly: "Monthly salary",
  daily: "Per day",
  hourly: "Per hour",
};

export const CADENCE_LABELS: Record<PaymentCadence, string> = {
  monthly: "Monthly",
  weekly: "Weekly",
  ad_hoc: "Whenever needed",
};

export const APPROVAL_MODE_LABELS: Record<ApprovalMode, string> = {
  geo_auto_approve: "Approve automatically at the centre",
  admin_approves_each: "I approve every check-in",
  self_auto_approve: "Approve every check-in automatically",
  admin_marks_only: "Only I mark attendance",
};

export const APPROVAL_MODE_HELP: Record<ApprovalMode, string> = {
  geo_auto_approve:
    "Check-ins inside the attendance location are approved. Check-ins from elsewhere wait for you.",
  admin_approves_each: "Every check-in waits in your approvals queue.",
  self_auto_approve: "Any check-in counts straight away, wherever it comes from.",
  admin_marks_only: "Self check-in is off. You mark days from the register.",
};

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  present: "Present",
  half_day: "Half day",
  absent: "Absent",
  leave: "Leave",
  holiday: "Holiday",
  week_off: "Week off",
};

/** One-letter code for the dense register grid. */
export const ATTENDANCE_STATUS_SHORT: Record<AttendanceStatus, string> = {
  present: "P",
  half_day: "½",
  absent: "A",
  leave: "L",
  holiday: "H",
  week_off: "W",
};

export const RECOVERY_PLAN_LABELS: Record<RecoveryPlan, string> = {
  same_month: "From this month's pay",
  next_month: "From next month's pay",
  emi: "In monthly instalments",
  manual: "Custom schedule",
};

export const ADJUSTMENT_KIND_LABELS: Record<AdjustmentKind, string> = {
  incentive: "Incentive",
  bonus: "Bonus",
  reimbursement: "Reimbursement",
  deduction: "Deduction",
};

export const PAYMENT_METHOD_LABELS: Record<TeamPaymentMethod, string> = {
  cash: "Cash",
  upi: "UPI",
  bank: "Bank transfer",
  other: "Other",
};

export const LEAVE_TYPE_LABELS: Record<LeaveType, string> = {
  casual: "Casual",
  sick: "Sick",
  earned: "Earned",
  unpaid: "Unpaid",
  other: "Other",
};

export const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

// ── Wire shapes ────────────────────────────────────────────────────────

export interface TeamPayPolicy {
  day_basis: DayBasis;
  daily_paid_holidays: boolean;
  default_approval_mode: ApprovalMode;
  geofence_lat: number | null;
  geofence_lng: number | null;
  geofence_radius_m: number;
  max_accuracy_m: number;
  location_label: string | null;
  default_recovery_plan: RecoveryPlan;
  default_emi_months: number;
  leave_year_start_month: number;
  show_pay_to_team: boolean;
  allow_self_check_in: boolean;
  allow_leave_requests: boolean;
  updated_at: string | null;
}

export type UpdatePayPolicyBody = Partial<
  Pick<
    TeamPayPolicy,
    | "day_basis"
    | "daily_paid_holidays"
    | "default_recovery_plan"
    | "default_emi_months"
    | "leave_year_start_month"
    | "show_pay_to_team"
    | "allow_leave_requests"
  >
>;

export interface UpdateAttendancePolicyBody {
  default_approval_mode?: ApprovalMode;
  geofence_lat?: number;
  geofence_lng?: number;
  geofence_radius_m?: number;
  max_accuracy_m?: number;
  location_label?: string | null;
  allow_self_check_in?: boolean;
  clear_geofence?: boolean;
}

export interface StaffPayTerms {
  pay_basis: PayBasis;
  daily_rate_paise: number | null;
  hourly_rate_paise: number | null;
  expected_hours_per_day: number | null;
  weekly_off_days: number[];
  paid_leave_per_month: number;
  paid_leave_per_year: number;
  joined_on: string | null;
  exited_on: string | null;
  payment_cadence: PaymentCadence;
  attendance_mode: ApprovalMode | null;
}

export interface TermsInput {
  pay_basis: PayBasis;
  monthly_salary_paise?: number | null;
  daily_rate_paise?: number | null;
  hourly_rate_paise?: number | null;
  expected_hours_per_day?: number | null;
  weekly_off_days: number[];
  paid_leave_per_month: number;
  paid_leave_per_year: number;
  joined_on?: string | null;
  exited_on?: string | null;
  payment_cadence: PaymentCadence;
  attendance_mode?: ApprovalMode | null;
}

export interface TeamMember {
  id: string;
  name: string;
  role_label: string;
  has_portal_access: boolean;
  linked_user_id: string | null;
  branch_id: string | null;
  is_active: boolean;
  note: string | null;
  monthly_amount_paise: number;
  terms: StaffPayTerms;
  monthly_salary_paise: number | null;
  attendance_mode: ApprovalMode;
  terms_configured: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateTeamMemberBody {
  name: string;
  role_label: string;
  has_portal_access?: boolean;
  linked_user_id?: string | null;
  branch_id?: string | null;
  note?: string | null;
  terms: TermsInput;
}

export interface UpdateTeamMemberBody {
  name?: string;
  role_label?: string;
  has_portal_access?: boolean;
  linked_user_id?: string | null;
  note?: string | null;
  is_active?: boolean;
  terms?: TermsInput;
}

export interface TermsAssistResponse {
  terms: TermsInput;
  rationale: string;
  source: "model" | "rules";
  notes: string[];
}

export interface TeamAttendance {
  id: string;
  staff_payee_id: string;
  member_name: string | null;
  day: string;
  status: AttendanceStatus;
  leave_paid: boolean;
  leave_fraction: number;
  source: AttendanceSource;
  approval_status: ApprovalStatus;
  check_in_at: string | null;
  check_out_at: string | null;
  hours_worked: number | null;
  distance_m: number | null;
  accuracy_m: number | null;
  inside_geofence: boolean | null;
  decided_by: string | null;
  decided_at: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface MarkAttendanceBody {
  staff_payee_id: string;
  day: string;
  status: AttendanceStatus;
  leave_paid?: boolean;
  leave_fraction?: number;
  hours_worked?: number | null;
  note?: string | null;
}

export interface BulkMarkBody {
  day: string;
  entries: { staff_payee_id: string; status: AttendanceStatus; leave_paid?: boolean }[];
  overwrite?: boolean;
}

export interface RegisterDay {
  day: string;
  status: AttendanceStatus | null;
  approval_status: ApprovalStatus | null;
  record_id: string | null;
  week_off: boolean;
  holiday: boolean;
}

export interface RegisterRow {
  staff_payee_id: string;
  name: string;
  role_label: string;
  days: RegisterDay[];
  present: number;
  absent: number;
  leave: number;
  pending: number;
}

export interface AttendanceRegister {
  year: number;
  month: number;
  holidays: string[];
  rows: RegisterRow[];
  pending_total: number;
}

export interface Instalment {
  year: number;
  month: number;
  amount_paise: number;
}

export interface RecoveryPlanInput {
  plan: RecoveryPlan;
  emi_months?: number | null;
  start_year?: number | null;
  start_month?: number | null;
  manual_schedule?: Instalment[];
}

export interface Adjustment {
  id: string;
  staff_payee_id: string;
  member_name: string | null;
  kind: AdjustmentKind;
  amount_paise: number;
  effective_on: string;
  note: string | null;
  created_at: string;
}

export interface Advance {
  id: string;
  staff_payee_id: string;
  member_name: string | null;
  amount_paise: number;
  given_on: string;
  method: TeamPaymentMethod;
  reference: string | null;
  plan: RecoveryPlan;
  emi_months: number | null;
  schedule: Instalment[];
  prepayments: {
    paid_on: string;
    amount_paise: number;
    method: TeamPaymentMethod;
    note: string | null;
  }[];
  recovered_paise: number;
  outstanding_paise: number;
  source_payment_id: string | null;
  note: string | null;
  created_at: string;
}

export interface AdvancePreview {
  plan: RecoveryPlan;
  schedule: Instalment[];
  summary: string;
}

export interface AllocationLine {
  target: AllocationTarget;
  amount_paise: number;
  year: number | null;
  month: number | null;
  label: string;
}

export interface PaymentPreviewBody {
  staff_payee_id: string;
  amount_paise: number;
  paid_on: string;
  mode: AllocationMode;
  advance_recovery?: RecoveryPlanInput | null;
}

export interface PaymentPreview {
  mode: AllocationMode;
  lines: AllocationLine[];
  salary_paise: number;
  dues_paise: number;
  advance_paise: number;
  summary: string;
  advance_schedule: Instalment[];
  advance_plan: RecoveryPlan | null;
  owed_before_paise: number;
  ledger_version: number;
}

export interface CreatePaymentBody extends PaymentPreviewBody {
  method: TeamPaymentMethod;
  reference?: string | null;
  note?: string | null;
  expected_ledger_version?: number | null;
}

export interface Payment {
  id: string;
  staff_payee_id: string;
  member_name: string | null;
  amount_paise: number;
  paid_on: string;
  method: TeamPaymentMethod;
  reference: string | null;
  note: string | null;
  allocation_mode: AllocationMode;
  allocation: AllocationLine[];
  allocation_summary: string;
  advance_id: string | null;
  created_at: string;
}

export type StatementLineKind =
  | "salary"
  | "incentive"
  | "bonus"
  | "reimbursement"
  | "deduction"
  | "advance_given"
  | "advance_recovery"
  | "advance_prepayment"
  | "payment";

export interface StatementLine {
  day: string;
  kind: StatementLineKind;
  label: string;
  amount_paise: number;
  detail_paise: number | null;
  reference_id: string | null;
}

export interface SalaryMonth {
  year: number;
  month: number;
  window_start: string | null;
  window_end: string | null;
  amount_paise: number;
  period_days: number;
  payable_units: number;
  explanation: string;
  present_days: number;
  half_days: number;
  absent_days: number;
  paid_leave_days: number;
  unpaid_leave_days: number;
  holidays: number;
  week_offs: number;
  hours: number;
}

export interface MemberStatement {
  staff_payee_id: string;
  member_name: string;
  pay_basis: PayBasis;
  period_from: string;
  period_to: string;
  opening_balance_paise: number;
  closing_balance_paise: number;
  earned_paise: number;
  additions_paise: number;
  deductions_paise: number;
  recoveries_paise: number;
  paid_paise: number;
  advance_outstanding_paise: number;
  months: SalaryMonth[];
  lines: StatementLine[];
}

export interface SummaryRow {
  staff_payee_id: string;
  name: string;
  role_label: string;
  pay_basis: PayBasis;
  is_active: boolean;
  earned_paise: number;
  additions_paise: number;
  deductions_paise: number;
  recovery_paise: number;
  paid_paise: number;
  advance_outstanding_paise: number;
  owed_today_paise: number;
  pending_attendance: number;
}

export interface TeamPaySummary {
  year: number;
  month: number;
  as_of: string;
  rows: SummaryRow[];
  totals: SummaryRow;
}

export interface CloseMonthResponse {
  pay_run_id: string;
  status: string;
  payslip_count: number;
  gross_total_paise: number;
  net_total_paise: number;
  skipped_members: string[];
}

export interface LeaveRequest {
  id: string;
  staff_payee_id: string;
  member_name: string | null;
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  half_day: boolean;
  reason: string | null;
  status: LeaveStatus;
  days_requested: number;
  paid_days: number;
  unpaid_days: number;
  decided_at: string | null;
  decision_note: string | null;
  created_at: string;
}

export interface LeaveBalance {
  staff_payee_id: string;
  leave_year_start: string;
  leave_year_end: string;
  accrued_days: number;
  used_paid_days: number;
  pending_days: number;
  available_days: number;
}

interface ListOf<T> {
  items: T[];
  total: number;
}

// ── Helpers ────────────────────────────────────────────────────────────

/** Today as an IST calendar day (`YYYY-MM-DD`). */
export function istToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** `YYYY-MM-DD` → `[year, month, day]`. */
export function parseDay(iso: string): [number, number, number] {
  const [y = 1970, m = 1, d = 1] = iso.split("-").map(Number);
  return [y, m, d];
}

/** Current IST `[year, month]`. */
export function istYearMonth(now: Date = new Date()): [number, number] {
  const [y, m] = parseDay(istToday(now));
  return [y, m];
}

export function monthLabel(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
}

export function shortMonthLabel(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", {
    month: "short",
    year: "numeric",
  });
}

/** `2026-10-05` → `Oct 05, 2026` (Iron Rule #16 date format). */
export function formatDay(iso: string): string {
  const [y, m, d] = parseDay(iso);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

export function monthBounds(year: number, month: number): { from: string; to: string } {
  const last = new Date(year, month, 0).getDate();
  const mm = String(month).padStart(2, "0");
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(last).padStart(2, "0")}` };
}

function toQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

// ── Query keys ─────────────────────────────────────────────────────────

const ROOT = ["ia", "team-pay"] as const;
const KEYS = {
  policy: [...ROOT, "policy"] as const,
  members: [...ROOT, "members"] as const,
  summary: [...ROOT, "summary"] as const,
  statement: [...ROOT, "statement"] as const,
  register: [...ROOT, "register"] as const,
  attendance: [...ROOT, "attendance"] as const,
  leaves: [...ROOT, "leaves"] as const,
  leaveBalance: [...ROOT, "leave-balance"] as const,
  advances: [...ROOT, "advances"] as const,
  payments: [...ROOT, "payments"] as const,
  adjustments: [...ROOT, "adjustments"] as const,
};

/** Team pay feeds payroll runs and the accounting P&L staff-cost line. */
function useInvalidateTeamPay() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ROOT });
    void qc.invalidateQueries({ queryKey: ["ia", "payroll"] });
    void qc.invalidateQueries({ queryKey: ["ia", "accounting"] });
  };
}

// ── Reads ──────────────────────────────────────────────────────────────

export function useTeamPayPolicy(enabled = true) {
  return useQuery<TeamPayPolicy>({
    queryKey: KEYS.policy,
    queryFn: () => api.get<TeamPayPolicy>("/api/team-pay/policy"),
    enabled,
    staleTime: 60_000,
  });
}

export function useTeamMembers(enabled = true) {
  return useQuery<ListOf<TeamMember>>({
    queryKey: KEYS.members,
    queryFn: () => api.get<ListOf<TeamMember>>("/api/team-pay/members"),
    enabled,
    staleTime: 30_000,
  });
}

export function useTeamPaySummary(year: number, month: number, enabled = true) {
  return useQuery<TeamPaySummary>({
    queryKey: [...KEYS.summary, year, month],
    queryFn: () => api.get<TeamPaySummary>(`/api/team-pay/summary${toQuery({ year, month })}`),
    enabled,
    staleTime: 15_000,
  });
}

export function useMemberStatement(payeeId: string | null, from: string, to: string) {
  return useQuery<MemberStatement>({
    queryKey: [...KEYS.statement, payeeId, from, to],
    queryFn: () =>
      api.get<MemberStatement>(
        `/api/team-pay/members/${payeeId}/statement${toQuery({ from, to })}`,
      ),
    enabled: payeeId !== null,
    staleTime: 10_000,
  });
}

export function useLeaveBalance(payeeId: string | null) {
  return useQuery<LeaveBalance>({
    queryKey: [...KEYS.leaveBalance, payeeId],
    queryFn: () => api.get<LeaveBalance>(`/api/team-pay/members/${payeeId}/leave-balance`),
    enabled: payeeId !== null,
    staleTime: 30_000,
  });
}

export function useAttendanceRegister(year: number, month: number, enabled = true) {
  return useQuery<AttendanceRegister>({
    queryKey: [...KEYS.register, year, month],
    queryFn: () =>
      api.get<AttendanceRegister>(`/api/team-pay/attendance/register${toQuery({ year, month })}`),
    enabled,
    staleTime: 15_000,
  });
}

export function usePendingAttendance(enabled = true) {
  const today = istToday();
  const [y, m] = istYearMonth();
  const from = monthBounds(...shiftMonth(y, m, -2)).from;
  return useQuery<ListOf<TeamAttendance>>({
    queryKey: [...KEYS.attendance, "pending", from, today],
    queryFn: () =>
      api.get<ListOf<TeamAttendance>>(
        `/api/team-pay/attendance${toQuery({ from, to: today, approval_status: "pending", limit: 500 })}`,
      ),
    enabled,
    staleTime: 10_000,
  });
}

export function useLeaveRequests(status: LeaveStatus | undefined, enabled = true) {
  return useQuery<ListOf<LeaveRequest>>({
    queryKey: [...KEYS.leaves, status ?? "all"],
    queryFn: () =>
      api.get<ListOf<LeaveRequest>>(`/api/team-pay/leave-requests${toQuery({ status })}`),
    enabled,
    staleTime: 15_000,
  });
}

export function useMemberAdvances(payeeId: string | null) {
  return useQuery<ListOf<Advance>>({
    queryKey: [...KEYS.advances, payeeId],
    queryFn: () =>
      api.get<ListOf<Advance>>(`/api/team-pay/advances${toQuery({ staff_payee_id: payeeId })}`),
    enabled: payeeId !== null,
    staleTime: 10_000,
  });
}

export function useMemberPayments(payeeId: string | null) {
  return useQuery<ListOf<Payment>>({
    queryKey: [...KEYS.payments, payeeId],
    queryFn: () =>
      api.get<ListOf<Payment>>(`/api/team-pay/payments${toQuery({ staff_payee_id: payeeId })}`),
    enabled: payeeId !== null,
    staleTime: 10_000,
  });
}

export function shiftMonth(year: number, month: number, delta: number): [number, number] {
  const d = new Date(year, month - 1 + delta, 1);
  return [d.getFullYear(), d.getMonth() + 1];
}

// ── Writes ─────────────────────────────────────────────────────────────

function useTeamPayMutation<TBody, TResult>(fn: (body: TBody) => Promise<TResult>) {
  const invalidate = useInvalidateTeamPay();
  return useMutation<TResult, ApiError, TBody>({ mutationFn: fn, onSuccess: invalidate });
}

export const useUpdatePayPolicy = () =>
  useTeamPayMutation((body: UpdatePayPolicyBody) =>
    api.put<TeamPayPolicy>("/api/team-pay/policy/pay", body),
  );

export const useUpdateAttendancePolicy = () =>
  useTeamPayMutation((body: UpdateAttendancePolicyBody) =>
    api.put<TeamPayPolicy>("/api/team-pay/policy/attendance", body),
  );

export const useCreateTeamMember = () =>
  useTeamPayMutation((body: CreateTeamMemberBody) =>
    api.post<TeamMember>("/api/team-pay/members", body),
  );

export const useUpdateTeamMember = () =>
  useTeamPayMutation(({ id, body }: { id: string; body: UpdateTeamMemberBody }) =>
    api.patch<TeamMember>(`/api/team-pay/members/${id}`, body),
  );

export const useDeleteTeamMember = () =>
  useTeamPayMutation((id: string) => api.delete<void>(`/api/team-pay/members/${id}`));

export function useAssistTerms() {
  return useMutation<TermsAssistResponse, ApiError, string>({
    mutationFn: (description) =>
      api.post<TermsAssistResponse>("/api/team-pay/members/assist", { description }),
  });
}

export const useMarkAttendance = () =>
  useTeamPayMutation((body: MarkAttendanceBody) =>
    api.put<TeamAttendance>("/api/team-pay/attendance", body),
  );

export const useBulkMarkAttendance = () =>
  useTeamPayMutation((body: BulkMarkBody) =>
    api.post<{ marked: number; skipped: number }>("/api/team-pay/attendance/bulk", body),
  );

export const useDeleteAttendance = () =>
  useTeamPayMutation((id: string) => api.delete<void>(`/api/team-pay/attendance/${id}`));

export const useDecideAttendance = () =>
  useTeamPayMutation(
    ({ ids, decision, note }: { ids: string[]; decision: "approve" | "reject"; note?: string }) =>
      api.post<{ updated: number; skipped: string[] }>("/api/team-pay/attendance/decisions", {
        ids,
        decision,
        note: note ?? null,
      }),
  );

export const useDecideLeave = () =>
  useTeamPayMutation(
    ({
      id,
      approve,
      note,
      force_paid,
    }: {
      id: string;
      approve: boolean;
      note?: string | null;
      force_paid?: boolean | null;
    }) =>
      api.post<LeaveRequest>(
        `/api/team-pay/leave-requests/${id}/${approve ? "approve" : "reject"}`,
        { note: note ?? null, force_paid: force_paid ?? null },
      ),
  );

export const useCreateAdjustment = () =>
  useTeamPayMutation(
    (body: {
      staff_payee_id: string;
      kind: AdjustmentKind;
      amount_paise: number;
      effective_on: string;
      note?: string | null;
    }) => api.post<Adjustment>("/api/team-pay/adjustments", body),
  );

export const useDeleteAdjustment = () =>
  useTeamPayMutation((id: string) => api.delete<void>(`/api/team-pay/adjustments/${id}`));

export function usePreviewAdvance() {
  return useMutation<
    AdvancePreview,
    ApiError,
    { amount_paise: number; given_on: string; recovery: RecoveryPlanInput | null }
  >({
    mutationFn: (body) => api.post<AdvancePreview>("/api/team-pay/advances/preview", body),
  });
}

export const useCreateAdvance = () =>
  useTeamPayMutation(
    (body: {
      staff_payee_id: string;
      amount_paise: number;
      given_on: string;
      method: TeamPaymentMethod;
      reference?: string | null;
      note?: string | null;
      recovery: RecoveryPlanInput | null;
    }) => api.post<Advance>("/api/team-pay/advances", body),
  );

export const useUpdateAdvancePlan = () =>
  useTeamPayMutation(({ id, recovery }: { id: string; recovery: RecoveryPlanInput }) =>
    api.put<Advance>(`/api/team-pay/advances/${id}/plan`, { recovery }),
  );

export const usePrepayAdvance = () =>
  useTeamPayMutation(
    ({
      id,
      ...body
    }: {
      id: string;
      amount_paise: number;
      paid_on: string;
      method: TeamPaymentMethod;
      note?: string | null;
    }) => api.post<Advance>(`/api/team-pay/advances/${id}/prepay`, body),
  );

export const useDeleteAdvance = () =>
  useTeamPayMutation((id: string) => api.delete<void>(`/api/team-pay/advances/${id}`));

export function usePreviewPayment() {
  return useMutation<PaymentPreview, ApiError, PaymentPreviewBody>({
    mutationFn: (body) => api.post<PaymentPreview>("/api/team-pay/payments/preview", body),
  });
}

export const useCreatePayment = () =>
  useTeamPayMutation((body: CreatePaymentBody) =>
    api.post<Payment>("/api/team-pay/payments", body),
  );

export const useVoidPayment = () =>
  useTeamPayMutation((id: string) => api.delete<void>(`/api/team-pay/payments/${id}`));

export const useCloseMonth = () =>
  useTeamPayMutation(
    ({ year, month, finalize }: { year: number; month: number; finalize: boolean }) =>
      api.post<CloseMonthResponse>(`/api/team-pay/months/${year}/${month}/close`, { finalize }),
  );
